import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Menu, Edit, Settings, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface CommunityCardV2Props {
  communityData?: {
    id: number;
    title: string;
    subtitle: string;
    imgSrc: string;
  };
  editCard?: () => void;
  openSettings?: () => void;
  deleteCommunity?: () => void;
}

export function CommunityCardV2({
  communityData,
  editCard,
  openSettings,
  deleteCommunity,
}: CommunityCardV2Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Return early if no data
  if (!communityData) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="p-6">
          <p className="text-muted-foreground text-center">
            No community data available
          </p>
        </CardContent>
      </Card>
    );
  }

  const data = communityData;

  const menuOptions = [
    { label: "Edit Community", icon: Edit, action: "edit-community" },
    {
      label: "Community Settings",
      icon: Settings,
      action: "community-settings",
    },
    { label: "Delete Community", icon: Trash2, action: "delete-community" },
  ];

  const handleMenuClick = (action: string) => {
    setIsMenuOpen(false);
    switch (action) {
      case "edit-community":
        editCard?.();
        break;
      case "community-settings":
        openSettings?.();
        break;
      case "delete-community":
        deleteCommunity?.();
        break;
      default:
        break;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Card className="w-full max-w-2xl mx-auto hover:shadow-lg transition-shadow duration-300 relative">
        <div className="absolute top-4 right-4 z-10">
          <motion.button
            className="p-2 hover:bg-muted rounded-md transition-colors flex justify-center items-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <Menu className="w-8 h-8" />
          </motion.button>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="absolute top-12 right-0 bg-white border rounded-lg shadow-lg min-w-48 py-2"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                {menuOptions.map((option, index) => {
                  const IconComponent = option.icon;
                  return (
                    <motion.button
                      key={option.action}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      onClick={() => handleMenuClick(option.action)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.1, delay: index * 0.05 }}
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                    >
                      <IconComponent className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{option.label}</span>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <CardHeader className="px-4 pt-4 pb-0 pr-16">
          <div className="flex items-center gap-4">
            <motion.div
              className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <img
                src={data.imgSrc}
                alt={`${data.title} logo`}
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              className="flex-1 min-w-0"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <CardTitle
                className="mb-1 truncate cursor-default"
                title={data.title}
              >
                {data.title.length > 25
                  ? `${data.title.substring(0, 25)}...`
                  : data.title}
              </CardTitle>
            </motion.div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {/* Description Section */}
          <motion.div
            className="flex flex-col items-center justify-center text-center space-y-2 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <label className="text-muted-foreground text-sm">Description</label>
            <motion.div
              className="p-3 bg-muted rounded-md hover:bg-muted/80 transition-colors w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              whileHover={{ scale: 1.01 }}
            >
              <p className="text-sm">{data.subtitle}</p>
            </motion.div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default CommunityCardV2;
